import React, { useEffect, useState } from 'react';
import API from '../services/api';
import './Batches.css';

const DEFAULT_BATCH_SECTIONS = [
  {
    key:   'arrear',
    label: 'Arrear Batches',
    names: ['No Arrears', '1-2 Arrears', '3+ Arrears'],
  },
  {
    key:   'performance',
    label: 'Performance Batches',
    names: ['High Performers', 'Average Performers', 'Needs Improvement'],
  },
  {
    key:   'branch',
    label: 'Branch Batches',
    names: ['CSE Branch','ECE Branch','EEE Branch','IT Branch','MECH Branch','CIVIL Branch'],
  },
];

const BATCH_COLORS = {
  'No Arrears':         { bg: '#e6f9f0', color: '#1a7f3c', border: '#b7ebc8' },
  'High Performers':    { bg: '#e6f9f0', color: '#1a7f3c', border: '#b7ebc8' },
  '1-2 Arrears':        { bg: '#fff8e6', color: '#b37400', border: '#ffe0a0' },
  'Average Performers': { bg: '#fff8e6', color: '#b37400', border: '#ffe0a0' },
  '3+ Arrears':         { bg: '#fff0f0', color: '#cc0000', border: '#ffcccc' },
  'Needs Improvement':  { bg: '#fff0f0', color: '#cc0000', border: '#ffcccc' },
  'CSE Branch':     { bg: '#eef2ff', color: '#3c5bd4', border: '#c5d0f5' },
  'ECE Branch':     { bg: '#eef2ff', color: '#3c5bd4', border: '#c5d0f5' },
  'EEE Branch':     { bg: '#eef2ff', color: '#3c5bd4', border: '#c5d0f5' },
  'IT Branch':      { bg: '#eef2ff', color: '#3c5bd4', border: '#c5d0f5' },
  'MECH Branch':    { bg: '#f3effe', color: '#8e44ad', border: '#c5b0e8' },
  'CIVIL Branch':   { bg: '#f3effe', color: '#8e44ad', border: '#c5b0e8' },
};

const getBatchColor = (name = '') =>
  BATCH_COLORS[name] || { bg: '#f0f4ff', color: '#0f3460', border: '#c5d0f5' };

const Batches = () => {
  const [batches, setBatches]             = useState([]);
  const [allStudents, setAllStudents]     = useState([]);
  const [loading, setLoading]             = useState(true);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [batchStudents, setBatchStudents] = useState([]);
  const [availableStudents, setAvailableStudents] = useState([]);

  // Panel modes: null | 'view' | 'create'
  const [mode, setMode]                   = useState(null);
  const [showEdit, setShowEdit]           = useState(false);
  const [showAddStudent, setShowAddStudent] = useState(false);

  // Create form
  const [createForm, setCreateForm] = useState({
    batch_name: '', batch_type: 'manual', description: '',
  });
  // Students selected for new batch
  const [createSelectedIds, setCreateSelectedIds] = useState([]);

  // Create panel filters
  const [createSearch, setCreateSearch]   = useState('');
  const [createBranch, setCreateBranch]   = useState('');
  const [createCgpa, setCreateCgpa]       = useState('');
  const [createArrear, setCreateArrear]   = useState('');

  // Edit form
  const [editForm, setEditForm] = useState({ batch_name: '', batch_type: '', description: '' });

  // View batch filters
  const [studentSearch, setStudentSearch] = useState('');
  const [branchFilter, setBranchFilter]   = useState('');
  const [cgpaFilter, setCgpaFilter]       = useState('');
  const [arrearFilter, setArrearFilter]   = useState('');
  const [searchAvail, setSearchAvail]     = useState('');

  // Batch list
  const [batchSearch, setBatchSearch]     = useState('');
  const [expandedSections, setExpandedSections] = useState({
    arrear: true, performance: true, branch: true, manual: true,
  });

  const [msg, setMsg] = useState('');

  useEffect(() => {
    fetchBatches();
    fetchAllStudents();
  }, []);

  const fetchBatches = async () => {
    try {
      const res = await API.get('/batches');
      setBatches(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const fetchAllStudents = async () => {
    try {
      const res = await API.get('/students');
      setAllStudents(res.data);
    } catch (err) { console.error(err); }
  };

  const fetchBatchStudents = async (id) => {
    try {
      const res = await API.get(`/batches/${id}/students`);
      setBatchStudents(res.data);
    } catch (err) { console.error(err); }
  };

  const fetchAvailable = async (id) => {
    try {
      const res = await API.get(`/batches/${id}/available`);
      setAvailableStudents(res.data);
    } catch (err) { console.error(err); }
  };

  // ── Handlers ─────────────────────────────────────────────────────────────
  const handleSelectBatch = async (batch) => {
    setSelectedBatch(batch);
    setMode('view');
    setShowEdit(false);
    setShowAddStudent(false);
    resetViewFilters();
    await fetchBatchStudents(batch.id);
  };

  const openCreate = () => {
    setMode('create');
    setSelectedBatch(null);
    setShowEdit(false);
    setShowAddStudent(false);
    setCreateForm({ batch_name: '', batch_type: 'manual', description: '' });
    setCreateSelectedIds([]);
    resetCreateFilters();
  };

  const handleCreateBatch = async () => {
    if (!createForm.batch_name.trim()) { setMsg('Batch name is required'); return; }
    try {
      const res = await API.post('/batches', createForm);
      const newBatchId = res.data.batch?.id;
      // Add selected students to the new batch
      if (newBatchId && createSelectedIds.length > 0) {
        for (const sid of createSelectedIds) {
          await API.post(`/batches/${newBatchId}/students`, { student_id: sid });
        }
      }
      setMsg(`Batch created with ${createSelectedIds.length} students`);
      fetchBatches();
      setMode(null);
    } catch (err) { setMsg('Failed to create batch'); }
  };

  const handleUpdateBatch = async () => {
    try {
      await API.put(`/batches/${selectedBatch.id}`, editForm);
      setMsg('Batch updated');
      fetchBatches();
      setSelectedBatch(prev => ({ ...prev, ...editForm }));
      setShowEdit(false);
    } catch (err) { setMsg('Failed to update batch'); }
  };

  const handleDeleteBatch = async (id) => {
    if (!window.confirm('Delete this batch? Students will NOT be deleted.')) return;
    try {
      await API.delete(`/batches/${id}`);
      setMsg('Batch deleted');
      fetchBatches();
      setSelectedBatch(null);
      setMode(null);
    } catch (err) { setMsg('Failed to delete'); }
  };

  const handleAddStudent = async (studentId) => {
    try {
      await API.post(`/batches/${selectedBatch.id}/students`, { student_id: studentId });
      fetchBatchStudents(selectedBatch.id);
      fetchAvailable(selectedBatch.id);
      fetchBatches();
    } catch (err) { alert('Failed to add student'); }
  };

  const handleRemoveStudent = async (studentId) => {
    if (!window.confirm('Remove student from this batch?')) return;
    try {
      await API.delete(`/batches/${selectedBatch.id}/students/${studentId}`);
      fetchBatchStudents(selectedBatch.id);
      fetchBatches();
    } catch (err) { alert('Failed to remove student'); }
  };

  const openAddStudent = async () => {
    setShowAddStudent(true);
    setShowEdit(false);
    await fetchAvailable(selectedBatch.id);
  };

  const toggleSection = (key) =>
    setExpandedSections(p => ({ ...p, [key]: !p[key] }));

  const resetViewFilters   = () => { setStudentSearch(''); setBranchFilter(''); setCgpaFilter(''); setArrearFilter(''); };
  const resetCreateFilters = () => { setCreateSearch(''); setCreateBranch(''); setCreateCgpa(''); setCreateArrear(''); };

  const toggleCreateStudent = (sid) =>
    setCreateSelectedIds(p => p.includes(sid) ? p.filter(x => x !== sid) : [...p, sid]);

  // ── Filter helpers ────────────────────────────────────────────────────────
  const applyFilters = (list, search, branch, cgpa, arrear) =>
    list.filter(s => {
      const txt  = s.name?.toLowerCase().includes(search.toLowerCase()) ||
                   s.roll_number?.toLowerCase().includes(search.toLowerCase());
      const br  = branch   ? s.branch === branch : true;
      const cg   = cgpa === 'high' ? parseFloat(s.cgpa) >= 8
                 : cgpa === 'mid'  ? parseFloat(s.cgpa) >= 6 && parseFloat(s.cgpa) < 8
                 : cgpa === 'low'  ? parseFloat(s.cgpa) < 6
                 : true;
      const ar   = arrear === '0'   ? parseInt(s.arrears) === 0
                 : arrear === '1-2' ? parseInt(s.arrears) >= 1 && parseInt(s.arrears) <= 2
                 : arrear === '3+'  ? parseInt(s.arrears) >= 3
                 : true;
      return txt && br && cg && ar;
    });

  const filteredBatchStudents = applyFilters(batchStudents,   studentSearch, branchFilter,   cgpaFilter,   arrearFilter);
  const filteredAvail         = applyFilters(availableStudents, searchAvail, '',           '',           '');
  const filteredCreateStudents = applyFilters(allStudents,    createSearch,  createBranch,   createCgpa,   createArrear);

  const viewActiveFilters   = [branchFilter, cgpaFilter, arrearFilter].filter(Boolean).length;
  const createActiveFilters = [createBranch, createCgpa, createArrear].filter(Boolean).length;

  const allBranches = [...new Set(allStudents.map(s => s.branch).filter(Boolean))];
  const batchBranches = [...new Set(batchStudents.map(s => s.branch).filter(Boolean))];

  const totalStudents = batches.reduce((s, b) => s + parseInt(b.student_count || 0), 0);

  // ── Batch list section renderer ───────────────────────────────────────────
  const renderSection = (sectionKey, label, batchList) => {
    const filtered = batchSearch
      ? batchList.filter(b => b.batch_name.toLowerCase().includes(batchSearch.toLowerCase()))
      : batchList;
    if (filtered.length === 0) return null;
    return (
      <div key={sectionKey} style={{ marginBottom: 6 }}>
        <div className="batch-section-label"
          onClick={() => toggleSection(sectionKey)}
          style={{ cursor: 'pointer', userSelect: 'none' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 9, color: '#aaa' }}>
              {expandedSections[sectionKey] ? '▼' : '▶'}
            </span>
            {label}
          </div>
          <span className="batch-section-count">{filtered.length}</span>
        </div>
        {expandedSections[sectionKey] && filtered.map(b => {
          const c = getBatchColor(b.batch_name);
          return (
            <div key={b.id}
              className={`batch-item ${selectedBatch?.id === b.id && mode === 'view' ? 'active' : ''}`}
              onClick={() => handleSelectBatch(b)}>
              <div className="batch-item-left">
                <div className="batch-dot" style={{ background: c.color }} />
                <div>
                  <div className="batch-item-name">{b.batch_name}</div>
                  <div className="batch-item-desc">{b.description || '—'}</div>
                </div>
              </div>
              <span className="batch-count-badge"
                style={{ background: c.bg, color: c.color, border: `1px solid ${c.border}` }}>
                {b.student_count || 0}
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  // ── Filter bar component ──────────────────────────────────────────────────
  const FilterBar = ({ search, setSearch, branch, setBranch, cgpa, setCgpa, arrear, setArrear, branches, placeholder, activeCount, onClear, total, filtered }) => (
    <div className="batch-student-filters">
      <input className="bsf-search" placeholder={placeholder || 'Search name or roll...'}
        value={search} onChange={e => setSearch(e.target.value)} />
      <select className="bsf-select" value={branch} onChange={e => setBranch(e.target.value)}>
        <option value="">All Branches</option>
        {branches.map(d => <option key={d} value={d}>{d}</option>)}
      </select>
      <select className="bsf-select" value={cgpa} onChange={e => setCgpa(e.target.value)}>
        <option value="">All CGPA</option>
        <option value="high">≥ 8.0</option>
        <option value="mid">6.0 – 7.9</option>
        <option value="low">&lt; 6.0</option>
      </select>
      <select className="bsf-select" value={arrear} onChange={e => setArrear(e.target.value)}>
        <option value="">All Arrears</option>
        <option value="0">No Arrears</option>
        <option value="1-2">1–2 Arrears</option>
        <option value="3+">3+ Arrears</option>
      </select>
      {activeCount > 0 && (
        <button onClick={onClear} style={{
          background: '#fff0f0', border: 'none', borderRadius: 7,
          padding: '7px 12px', fontSize: 12, color: '#cc0000', cursor: 'pointer', fontWeight: 500,
        }}>
          Clear ({activeCount})
        </button>
      )}
      <span style={{ marginLeft: 'auto', fontSize: 12, color: '#888', alignSelf: 'center' }}>
        {filtered} / {total} students
      </span>
    </div>
  );

  // ── Student row component ─────────────────────────────────────────────────
  const StudentRow = ({ s, i, actionLabel, actionStyle, onAction, selected }) => (
    <div className="batch-student-row"
      style={selected ? { background: '#eef2ff', border: '1px solid #c5d0f5' } : {}}>
      <div className="bs-num">{i + 1}</div>
      <div className="bs-avatar" style={{
        background: selected ? '#eef2ff' : '#f0f2f5',
        color: selected ? '#3c5bd4' : '#555',
      }}>
        {s.name?.[0]?.toUpperCase()}
      </div>
      <div className="bs-info">
        <div className="bs-name">{s.name}</div>
        <div className="bs-sub">{s.roll_number} · {s.branch}</div>
      </div>
      <div className="bs-cgpa">
        <div className="bs-cgpa-val">{s.cgpa || '—'}</div>
        <div className="bs-cgpa-label">CGPA</div>
      </div>
      <div className="bs-arrears"
        style={{ color: parseInt(s.arrears) > 0 ? '#e94560' : '#16a085', fontWeight: 600, fontSize: 12 }}>
        {s.arrears ?? 0} arrears
      </div>
      <span style={{
        fontSize: 11, padding: '2px 9px', borderRadius: 20, fontWeight: 600,
        background: parseInt(s.arrears) === 0 ? '#e6f9f0' : parseInt(s.arrears) <= 2 ? '#fff8e6' : '#fff0f0',
        color:      parseInt(s.arrears) === 0 ? '#1a7f3c' : parseInt(s.arrears) <= 2 ? '#b37400' : '#cc0000',
      }}>
        {parseInt(s.arrears) === 0 ? 'Clear' : parseInt(s.arrears) <= 2 ? 'Low' : 'High'}
      </span>
      <button onClick={() => onAction(s.id)} style={actionStyle}>
        {actionLabel}
      </button>
    </div>
  );

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <div className="batches-page-content">

        <div className="top-bar">
          <div>
            <h2>Batch Management</h2>
            <p>{batches.length} batches · {totalStudents} students assigned</p>
          </div>
          <button className="btn-add" onClick={openCreate}>+ New Batch</button>
        </div>

        {msg && (
          <div className="msg-banner">
            {msg}
            <button onClick={() => setMsg('')} className="msg-close">×</button>
          </div>
        )}

        <div className="batches-layout">

          {/* ── Left: batch list ── */}
          <div className="batch-list-panel">
            <input className="batch-list-search" placeholder="Search batches..."
              value={batchSearch} onChange={e => setBatchSearch(e.target.value)} />

            {loading ? <div className="loading-sm">Loading...</div> : (
              <>
                {DEFAULT_BATCH_SECTIONS.map(sec => {
                  const defaultBatches = batches.filter(b => b.batch_type === 'auto');
                  const matched = defaultBatches.filter(b =>
                    sec.names.includes(b.batch_name)
                  );
                  return renderSection(sec.key, sec.label, matched);
                })}
                {renderSection('manual', 'Custom Batches', batches.filter(b => b.batch_type !== 'auto'))}
                {batches.length === 0 && !loading && (
                  <div className="empty-batch">
                    No batches yet. Run the SQL seed in pgAdmin to create the 12 default batches.
                  </div>
                )}
              </>
            )}
          </div>

          {/* ── Right: detail panel ── */}
          <div className="batch-detail-panel">

            {/* ════════════════════════════════ */}
            {/* CREATE NEW BATCH                 */}
            {/* ════════════════════════════════ */}
            {mode === 'create' && (
              <div className="detail-card">

                <div className="detail-header">
                  <h3>Create New Batch</h3>
                  <button className="icon-btn" onClick={() => setMode(null)}>×</button>
                </div>

                {/* Batch name + description */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 16 }}>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label>Batch Name *</label>
                    <input placeholder="e.g. SQL Focus Group"
                      value={createForm.batch_name}
                      onChange={e => setCreateForm(p => ({ ...p, batch_name: e.target.value }))} />
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label>Batch Type</label>
                    <select value={createForm.batch_type}
                      onChange={e => setCreateForm(p => ({ ...p, batch_type: e.target.value }))}>
                      <option value="manual">Manual</option>
                      <option value="performance">Performance Based</option>
                      <option value="custom">Custom</option>
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <textarea placeholder="Describe the purpose of this batch..."
                    rows={2}
                    value={createForm.description}
                    onChange={e => setCreateForm(p => ({ ...p, description: e.target.value }))} />
                </div>

                {/* Divider */}
                <div style={{ borderTop: '1.5px solid #f0f0f0', margin: '16px 0 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 14 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#1a1a2e' }}>
                    Add Students
                    {createSelectedIds.length > 0 && (
                      <span style={{ marginLeft: 8, background: '#0f3460', color: 'white', padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600 }}>
                        {createSelectedIds.length} selected
                      </span>
                    )}
                  </div>
                  <span style={{ fontSize: 12, color: '#888' }}>
                    Click + to add students to this batch
                  </span>
                </div>

                {/* Filters for student picker */}
                <FilterBar
                  search={createSearch}   setSearch={setCreateSearch}
                  branch={createBranch}   setBranch={setCreateBranch}
                  cgpa={createCgpa}       setCgpa={setCreateCgpa}
                  arrear={createArrear}   setArrear={setCreateArrear}
                  branches={allBranches}
                  placeholder="Search students..."
                  activeCount={createActiveFilters}
                  onClear={resetCreateFilters}
                  total={allStudents.length}
                  filtered={filteredCreateStudents.length}
                />

                {/* Student picker list */}
                <div className="students-in-batch" style={{ maxHeight: 340 }}>
                  {filteredCreateStudents.length === 0 ? (
                    <div className="empty-batch-students">No students match the filters.</div>
                  ) : filteredCreateStudents.map((s, i) => (
                    <StudentRow
                      key={s.id} s={s} i={i}
                      selected={createSelectedIds.includes(s.id)}
                      actionLabel={createSelectedIds.includes(s.id) ? '✓ Added' : '+ Add'}
                      actionStyle={{
                        padding: '5px 12px',
                        background: createSelectedIds.includes(s.id) ? '#e6f9f0' : '#0f3460',
                        color:      createSelectedIds.includes(s.id) ? '#1a7f3c' : 'white',
                        border:     createSelectedIds.includes(s.id) ? '1px solid #b7ebc8' : 'none',
                        borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                      }}
                      onAction={toggleCreateStudent}
                    />
                  ))}
                </div>

                {/* Action buttons */}
                <div className="form-actions" style={{ marginTop: 16 }}>
                  <button className="btn-cancel" onClick={() => setMode(null)}>Cancel</button>
                  <button className="btn-submit" onClick={handleCreateBatch}>
                    Create Batch
                    {createSelectedIds.length > 0 && ` (${createSelectedIds.length} students)`}
                  </button>
                </div>
              </div>
            )}

            {/* ════════════════════════════════ */}
            {/* VIEW BATCH DETAIL                */}
            {/* ════════════════════════════════ */}
            {mode === 'view' && selectedBatch && (() => {
              const c = getBatchColor(selectedBatch.batch_name);
              return (
                <div className="detail-card">

                  {/* Header */}
                  <div className="detail-header">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <div style={{
                        width: 42, height: 42, borderRadius: 10,
                        background: c.bg, border: `2px solid ${c.border}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 18, fontWeight: 700, color: c.color, flexShrink: 0,
                      }}>
                        {selectedBatch.batch_name?.[0]?.toUpperCase()}
                      </div>
                      <div>
                        <h3 style={{ margin: 0 }}>{selectedBatch.batch_name}</h3>
                        <p style={{ margin: 0, fontSize: 13, color: '#888' }}>{selectedBatch.description}</p>
                      </div>
                    </div>
                    <div className="detail-header-actions">
                      <button className="btn-sm-edit" onClick={() => {
                        setEditForm({ batch_name: selectedBatch.batch_name, batch_type: selectedBatch.batch_type, description: selectedBatch.description || '' });
                        setShowEdit(p => !p);
                        setShowAddStudent(false);
                      }}>
                        {showEdit ? 'Cancel Edit' : 'Edit'}
                      </button>
                      <button className="btn-sm-delete" onClick={() => handleDeleteBatch(selectedBatch.id)}>
                        Delete
                      </button>
                    </div>
                  </div>

                  {/* Stats pills */}
                  <div className="batch-meta" style={{ marginBottom: 14 }}>
                    <span className="meta-pill" style={{ background: c.bg, color: c.color, border: `1px solid ${c.border}` }}>
                      {selectedBatch.batch_type}
                    </span>
                    <span className="meta-pill blue">{batchStudents.length} students</span>
                    {batchStudents.filter(s => parseFloat(s.cgpa) >= 8).length > 0 && (
                      <span className="meta-pill" style={{ background: '#e6f9f0', color: '#1a7f3c' }}>
                        {batchStudents.filter(s => parseFloat(s.cgpa) >= 8).length} high CGPA
                      </span>
                    )}
                    {batchStudents.filter(s => parseInt(s.arrears) > 0).length > 0 && (
                      <span className="meta-pill" style={{ background: '#fff0f0', color: '#cc0000' }}>
                        {batchStudents.filter(s => parseInt(s.arrears) > 0).length} with arrears
                      </span>
                    )}
                  </div>

                  {/* Inline edit */}
                  {showEdit && (
                    <div className="edit-form-inline">
                      <div className="form-group">
                        <label>Batch Name</label>
                        <input value={editForm.batch_name}
                          onChange={e => setEditForm(p => ({ ...p, batch_name: e.target.value }))} />
                      </div>
                      <div className="form-group">
                        <label>Description</label>
                        <textarea rows={2} value={editForm.description}
                          onChange={e => setEditForm(p => ({ ...p, description: e.target.value }))} />
                      </div>
                      <div className="form-actions">
                        <button className="btn-cancel" onClick={() => setShowEdit(false)}>Cancel</button>
                        <button className="btn-submit" onClick={handleUpdateBatch}>Save Changes</button>
                      </div>
                    </div>
                  )}

                  {/* Add student panel */}
                  {showAddStudent && (
                    <div className="add-student-panel">
                      <div className="asp-header">
                        <span>Add Students to Batch</span>
                        <button className="icon-btn" onClick={() => setShowAddStudent(false)}>×</button>
                      </div>
                      <input className="asp-search" placeholder="Search by name or roll..."
                        value={searchAvail} onChange={e => setSearchAvail(e.target.value)} />
                      <div className="asp-list">
                        {filteredAvail.length === 0 ? (
                          <div className="asp-empty">
                            {availableStudents.length === 0 ? 'All students already in this batch' : 'No students match'}
                          </div>
                        ) : filteredAvail.map(s => (
                          <div key={s.id} className="asp-item">
                            <div>
                              <div className="asp-name">{s.name}</div>
                              <div className="asp-info">{s.roll_number} · {s.branch} · CGPA {s.cgpa || '—'} · {s.arrears || 0} arrears</div>
                            </div>
                            <button className="btn-add-sm" onClick={() => handleAddStudent(s.id)}>+ Add</button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Students header */}
                  <div className="students-in-batch-header">
                    <span style={{ fontWeight: 600 }}>
                      Students ({filteredBatchStudents.length}{viewActiveFilters > 0 ? ` of ${batchStudents.length}` : ''})
                    </span>
                    <button className="btn-add-student" onClick={openAddStudent}>+ Add Student</button>
                  </div>

                  {/* Filters */}
                  <FilterBar
                    search={studentSearch}   setSearch={setStudentSearch}
                    branch={branchFilter}    setBranch={setBranchFilter}
                    cgpa={cgpaFilter}        setCgpa={setCgpaFilter}
                    arrear={arrearFilter}    setArrear={setArrearFilter}
                    branches={batchBranches}
                    activeCount={viewActiveFilters}
                    onClear={resetViewFilters}
                    total={batchStudents.length}
                    filtered={filteredBatchStudents.length}
                  />

                  {/* Student list */}
                  <div className="students-in-batch">
                    {batchStudents.length === 0 ? (
                      <div className="empty-batch-students">No students yet. Click "+ Add Student".</div>
                    ) : filteredBatchStudents.length === 0 ? (
                      <div className="empty-batch-students">No students match the filters.</div>
                    ) : filteredBatchStudents.map((s, i) => (
                      <StudentRow
                        key={s.id} s={s} i={i}
                        actionLabel="Remove"
                        actionStyle={{
                          padding: '5px 12px', background: '#fff0f0',
                          border: '1px solid #ffcccc', borderRadius: 7,
                          fontSize: 12, color: '#cc0000', cursor: 'pointer', fontWeight: 500,
                        }}
                        onAction={handleRemoveStudent}
                      />
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* Empty state */}
            {mode === null && (
              <div className="empty-detail">
                <div className="empty-icon">◫</div>
                <p>Select a batch to view its students</p>
                <p className="empty-sub">
                  12 default batches are available on the left.<br />
                  Click "+ New Batch" to create a custom batch.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Batches;
